import dayjs from 'dayjs'
import { classificationName, loadClassification } from '@includes/classification'

/*
 * Les états d'une commande (OrderStatus côté serveur) : aucune route ne les liste, ils servent au
 * filtre de la liste. Ceux où il reste quelque chose à faire, d'abord.
 */
export const ORDER_STATUSES = ['pending-approval', 'open', 'in-service', 'rejected', 'cancelled']
export const OPEN_STATUSES = ['pending-approval', 'open']

/*
 * Ce qui se commande pour l'organisation elle-même, sans personne derrière (EquipmentKind::allows
 * côté serveur, qui reste juge) : le reste ne se commande que pour un membre. Pas de compteur : il
 * ne sert qu'à rembourser la recharge à domicile.
 */
export const ORGANIZATION_KINDS = ['charging-point']

/* Ce qui se pose (une date, un technicien, une adresse) : la borne et le compteur. */
export const INSTALLABLE_KINDS = ['charging-point', 'meter-device']

/* Les véhicules (EquipmentKind::isVehicle) : connectés, ou compteurs de remboursement. */
export const VEHICLE_KINDS = ['connected-vehicle', 'vehicle-meter']

/*
 * Les types de licence qu'un équipement posé peut porter (EquipmentLicences::LICENCE_TYPES côté
 * serveur, qui reste juge) : supervision, remboursement ou fourniture d'énergie pour une borne,
 * remboursement pour un compteur.
 */
export const LICENCE_TYPES = {
  'charging-point': ['charging-point', 'device', 'energy-provider'],
  'meter-device': ['device'],
}

/* Le type de licence qui rembourse (`device`) ; les autres supervisent ou fournissent l'énergie. */
export const REFUND_LICENCE = 'device'

/* Les prérequis qui reviennent à la personne (OrderStep::PERSON_REQUIREMENTS côté serveur), dans l'ordre où elle les lève. */
export const PERSON_REQUIREMENTS = ['membership', 'payment-method', 'deposit', 'location', 'confirmation', 'consent', 'connection']

/** Ce que la liste, la fiche, les dialogues et le portail terrain lisent de la même façon dans une commande d'équipement. */
export default {
  computed: {
    /** Gérer le module : décider, confier une pose, poser. */
    mayManageOnboarding() {
      return (this.currentUser?.permissions || []).includes('ONBOARDING_MODULE_MANAGE')
    },
    /** Suivre les commandes : planifier, relancer, dire où et qui appeler (aussi le gestionnaire qui commande). */
    mayFollowOrders() {
      const permissions = this.currentUser?.permissions || []
      return permissions.includes('ONBOARDING_MODULE_MANAGE') || permissions.includes('COMMISSIONING_REQUEST')
    },
  },

  methods: {
    /** L'état d'une borne en mots (« Accepté »), par les libellés de l'application ; tel quel s'il n'en a pas. */
    chargingPointStatusLabel(status) {
      if (!status) return '—'
      const key = `enums.charging-point-status.${status}`
      return this.$te(key) ? this.$t(key) : status
    },

    /**
     * Ce qui est dit sur place, sous la forme qu'une lecture (GET) transporte : les objets à plat,
     * en crochets — l'adresse et ses coordonnées comprises. L'écriture (POST) garde les objets.
     */
    asQuery(body) {
      const params = {}
      const flatten = (prefix, value) => {
        if (value && typeof value === 'object') {
          Object.entries(value).forEach(([key, v]) => flatten(`${prefix}[${key}]`, v))
        } else if (value !== null && value !== undefined && value !== '') {
          params[prefix] = value
        }
      }
      Object.entries(body || {}).forEach(([key, value]) => {
        if (value && typeof value === 'object') {
          flatten(key, value)
        } else if (value !== null && value !== undefined && value !== '') {
          params[key] = value
        }
      })
      return params
    },

    personName(order) {
      // La commande garde le nom tel que saisi à l'inscription ; on l'écrit comme la fiche client (Prénom NOM).
      const p = order.person || {}
      const firstname = (p.firstname || '').trim().toLowerCase().replace(/(^|[\s-])\p{L}/gu, c => c.toUpperCase())
      const lastname = (p.lastname || '').trim().toUpperCase()
      return [firstname, lastname].filter(Boolean).join(' ') || p.email || '—'
    },

    /** Pour qui : la personne d'une commande de membre, l'organisation sinon. */
    beneficiaryOf(order) {
      return order.holder === 'member' ? this.personName(order) : (order.organizationName || '—')
    },

    formatAddress(a) {
      if (!a) return ''
      const street = [a.number, a.street].filter(Boolean).join(' ')
      const city = [a.zipcode, a.city].filter(Boolean).join(' ')
      return [street, a.additionalInformation, city].filter(Boolean).join(', ')
    },

    /** Où l'équipement sera posé : l'adresse à créer, l'emplacement désigné, l'adresse de la personne, sinon « à préciser ». */
    addressOf(order) {
      if (order.newLocation) return this.formatAddress(order.newLocation)
      if (order.locationId) return order.locationName || '—'
      return this.formatAddress(order.person?.postalAddress) || this.$t('onboarding.commissioning.location-unknown')
    },

    /** « AC · Marques : … · Modèles : … », ou rien. */
    restrictionsOf(order) {
      const r = order.restrictions || {}
      if ((r.models || []).length) {
        // Les noms arrivent avec le classement (chargé une fois) ; d'ici là, les identifiants.
        loadClassification().catch(() => {})
      }
      return [
        r.currentType ? this.$t(`onboarding.commissioning.current-type.${r.currentType}`) : null,
        (r.vendors || []).length ? `${this.$t('onboarding.commissioning.form.vendors')} : ${r.vendors.join(', ')}` : null,
        // Une borne garde des gammes ou des types (leurs identifiants) : on les nomme.
        (r.models || []).length ? `${this.$t('onboarding.commissioning.form.models')} : ${r.models.map(classificationName).join(', ')}` : null,
      ].filter(Boolean).join(' · ')
    },

    /**
     * À qui d'agir sur une commande en cours : la validation, la personne (adresse, accord, connexion, paiement), la
     * logistique ZEborne (préparer, poster un badge), le gestionnaire de flotte (remettre un badge), la planification
     * puis le technicien (une pose). null : rien n'attend (en service, close).
     */
    actorOf(order) {
      // L'étape tenue par le serveur fait foi (la même que la file de « Commandes en attente »).
      if (order.step) {
        return {
          approval: 'approval', person: 'person', activation: 'person', shipment: 'logistics', handover: 'manager',
          survey: 'survey', scheduling: 'planning', installation: 'technician', commissioning: 'system',
        }[order.step] || null
      }
      if (order.status === 'pending-approval') return 'approval'
      if (order.status !== 'open') return null
      const pending = this.pendingRequirements(order)
      if (pending.some(r => PERSON_REQUIREMENTS.includes(r))) return 'person'
      // Les badges sont partis ou remis : c'est la personne qui les active en les recevant.
      if (pending.includes('activation') && !pending.includes('shipment') && !pending.includes('handover')) return 'person'
      if (pending.includes('shipment')) return 'logistics'
      if (pending.includes('handover')) return 'manager'
      if (pending.includes('installation')) return order.scheduledAt || order.technician ? 'technician' : 'planning'
      return 'system'
    },

    /**
     * Qui doit faire quoi, en une phrase : la validation, la personne, la logistique, le gestionnaire, la visite, la
     * pose, ou une mise en service arrêtée. Vide pour une commande close (son motif se dit à part).
     */
    expectedActionOf(o) {
      const a = (key, params = {}) => this.$t(`onboarding.commissioning.action.${key}`, params)
      if (o.status === 'pending-approval') return a('pending-approval')
      if (o.status === 'in-service') return a('in-service')
      if (!this.isOpenOrder(o)) return ''
      const actor = this.actorOf(o)
      if (actor === 'person') {
        const tasks = this.pendingRequirements(o).filter(r => [...PERSON_REQUIREMENTS, 'activation'].includes(r))
        return a('person', { person: this.personName(o), tasks: tasks.map(r => this.$t(`onboarding.commissioning.person-task.${r}`)).join(', ') })
      }
      if (actor === 'logistics') return a('logistics-prepare')
      if (actor === 'manager') return a('manager', { organization: o.organizationName || '' })
      if (actor === 'survey') return a('survey')
      // Arrêtée sur une étape, ou jamais partie (aucun plan) : ce n'est pas le même geste.
      if (actor === 'system') return (o.plan || o.planSummary) ? a('commissioning') : a('commissioning-not-started')
      if (o.installation) return a(`installation-${o.installation}`)
      return a('installation-to-schedule')
    },

    actorColor(actor) {
      return { approval: 'amber darken-2', person: 'blue-grey', logistics: 'deep-orange', manager: 'purple', planning: 'indigo', survey: 'indigo', technician: 'teal', system: 'red darken-1' }[actor] || 'grey'
    },

    kindLabel(kind) {
      const key = `onboarding.commissioning.kind.${kind}`
      return this.$te(key) ? this.$t(key) : kind
    },

    /** « Borne — membre », « Compteur — organisation »… */
    kindOf(order) {
      return this.$t(`onboarding.commissioning.kind-holder.${order.holder}`, { kind: this.kindLabel(order.kind) })
    },

    kindIcon(kind) {
      return {
        'charging-point': 'mdi-ev-station',
        'meter-device': 'mdi-meter-electric-outline',
        linky: 'mdi-flash-outline',
        'connected-vehicle': 'mdi-car-connected',
        'vehicle-meter': 'mdi-car-electric-outline',
        badge: 'mdi-card-account-details-outline',
      }[kind] || 'mdi-package-variant'
    },

    statusLabel(status) {
      return this.$t(`onboarding.commissioning.status.${status}`)
    },

    statusColor(status) {
      return {
        'pending-approval': 'orange darken-2',
        open: 'blue darken-2',
        'in-service': 'green darken-2',
        rejected: 'red darken-2',
        cancelled: 'grey',
      }[status] || 'grey'
    },

    /** Se pose-t-elle ? Une borne, un compteur : un technicien passe. Un véhicule, des badges : non. */
    isInstallable(order) {
      return (order.requirements || []).some(r => r.requirement === 'installation')
    },

    isOpenOrder(order) {
      return OPEN_STATUSES.includes(order?.status)
    },

    /** Ce qui reste à lever, dans l'ordre que donne la commande. */
    pendingRequirements(order) {
      return (order.requirements || []).filter(r => !r.satisfiedAt).map(r => r.requirement)
    },

    /** Le motif d'une clôture : ce que le gestionnaire a écrit, ou, sans motif, ce que le serveur a posé (traduit). */
    closingReasonLabel(reason) {
      if (!reason) return '-'
      const key = `onboarding.commissioning.closing-reason.${reason}`
      return this.$te(key) ? this.$t(key) : reason
    },

    requirementLabel(requirement) {
      return this.$t(`onboarding.commissioning.requirement.${requirement}`)
    },

    /** Une date seule (souhaitée, de visite) : l'heure n'y dit rien. */
    dayOf(date) {
      return date ? dayjs(date).format('DD/MM/YYYY') : ''
    },

    /** Le problème signalé par le technicien, en mots : la raison, puis sa note. */
    fieldIssueLabel(issue) {
      if (!issue) return ''
      const reason = this.$t(`onboarding.field.issue.reason.${issue.reason}`)
      return issue.note ? `${reason} — ${issue.note}` : reason
    },

    /** Le geste d'un plan, par son nom (« place-meter-device ») : son libellé quand l'écran le connaît. */
    planStepLabel(name) {
      const key = `commissioning.step.${name}`
      return this.$te(key) ? this.$t(key) : name
    },

    /** La raison d'un échec : une clé traduite, ou le texte tel quel. */
    planReasonLabel(reason, params) {
      if (!reason) return ''
      return this.$te(reason) ? this.$t(reason, params || {}) : reason
    },

    /**
     * Le message d'un refus : le serveur l'a déjà traduit (`message`) ; une clé que l'écran connaît
     * aussi passe par lui, avec ses valeurs.
     */
    messageOf(e) {
      const data = e?.response?.data
      const key = data?.messageKey
      if (key && this.$te(key)) return this.$t(key, data.parameters || {})
      return data?.message || this.$t('error-occurred')
    },
  },
}
